/**
 * Traceur de performance côté client — DÉSACTIVÉ PAR DÉFAUT.
 *
 * Objectif : rendre visible, sans relire le code, la chaîne de requêtes
 * sérielles payée au premier accès à /app (session Supabase → membership →
 * entities/settings/integration), le nombre d'aller-retours de `fetchAll`
 * et les Web Vitals réels de la machine de l'utilisateur.
 *
 * Interrupteur (CLAUDE.md interdit les `console.log` laissés en prod) :
 *   - `?debug=perf` dans l'URL, OU
 *   - `localStorage.setItem("encrelio:debug", "perf")`
 * Éteint : chaque appel fait un early-return, RIEN n'est écrit en console,
 * aucun buffer n'est alimenté, aucun `PerformanceObserver` n'est posé.
 *
 * Zéro dépendance npm : `performance.mark`/`measure` sont doublés pour que
 * les mesures apparaissent aussi dans l'onglet Performance de Chrome.
 *
 * Confidentialité : seuls des NOMS et des COMPTEURS sont conservés (jamais
 * le contenu des lignes). Les noms passent par `sanitizePerfName()` qui
 * masque UUID et emails, car les clés TanStack Query contiennent l'id
 * d'organisation.
 */

export const PERF_STORAGE_KEY = "encrelio:debug";
export const PERF_FLAG = "perf";
/** Buffer borné : une session longue ne doit pas fuir en mémoire. */
export const MAX_PERF_ENTRIES = 500;
/** Préfixe des marks/measures natifs, pour les repérer dans Chrome. */
const NATIVE_PREFIX = "enc:";
/** Tolérance de jitter pour considérer 2 spans comme enchaînés (ms). */
const SERIAL_TOLERANCE_MS = 2;

export type PerfMetaValue = string | number | boolean | null;
export type PerfMeta = Record<string, PerfMetaValue>;

export interface PerfSpan {
  kind: "span";
  name: string;
  startMs: number;
  endMs: number;
  durationMs: number;
  meta: PerfMeta;
}

export interface PerfMark {
  kind: "mark";
  name: string;
  startMs: number;
  meta: PerfMeta;
}

export type PerfEntry = PerfSpan | PerfMark;

export interface PerfSummary {
  spanCount: number;
  markCount: number;
  droppedEntries: number;
  firstSpanStartMs: number | null;
  lastSpanEndMs: number | null;
  /** Fenêtre totale couverte par les spans (dernière fin − premier début). */
  spanWallMs: number | null;
  /** Noms de la plus longue chaîne de spans strictement enchaînés. */
  serialChain: string[];
  /** Durée cumulée de cette chaîne : le coût de la sérialité. */
  serialChainMs: number;
  /** Nombre de spans qui chevauchent au moins un autre span. */
  parallelSpanCount: number;
  /** Fin du premier span ayant réellement ramené des lignes (meta.rows > 0). */
  timeToFirstDataMs: number | null;
}

export interface PerfResourceTiming {
  name: string;
  durationMs: number;
  transferBytes: number | null;
  decodedBytes: number | null;
}

export interface PerfVitals {
  ttfbMs: number | null;
  domContentLoadedMs: number | null;
  fcpMs: number | null;
  lcpMs: number | null;
  longTaskCount: number;
  longTaskTotalMs: number;
  scripts: PerfResourceTiming[];
  /** Types d'entrées refusés par le navigateur (Safari notamment). */
  unsupported: string[];
}

export interface PerfReport {
  enabled: boolean;
  generatedAtMs: number;
  location: string | null;
  summary: PerfSummary;
  vitals: PerfVitals;
  entries: PerfEntry[];
}

export interface PerfApi {
  readonly enabled: boolean;
  start(name: string, meta?: PerfMeta): void;
  end(name: string, meta?: PerfMeta): void;
  mark(name: string, meta?: PerfMeta): void;
  record(name: string, startMs: number, endMs: number, meta?: PerfMeta): void;
  timeline(): PerfEntry[];
  report(): PerfReport;
  toJSON(): string;
  clear(): void;
  version(): number;
  subscribe(listener: () => void): () => void;
  /** Coupe le traçage et efface le flag persisté (bouton de l'overlay). */
  disable(): void;
}

declare global {
  interface Window {
    __encrelioPerf?: PerfApi;
  }
}

// ---------------------------------------------------------------------------
// Interrupteur
// ---------------------------------------------------------------------------

/** Logique pure de l'interrupteur — testable sans navigateur. */
export function parsePerfSwitch(
  search: string,
  storedValue: string | null,
): boolean {
  if (storedValue?.trim() === PERF_FLAG) return true;
  if (!search) return false;
  try {
    const raw = new URLSearchParams(search).get("debug");
    if (!raw) return false;
    return raw
      .split(",")
      .map((part) => part.trim())
      .includes(PERF_FLAG);
  } catch {
    return false;
  }
}

let enabledCache: boolean | null = null;

function readStoredFlag(): string | null {
  try {
    return window.localStorage.getItem(PERF_STORAGE_KEY);
  } catch {
    // Safari en navigation privée / stockage bloqué.
    return null;
  }
}

/**
 * `true` si le traçage est demandé. Mémoïsé : appelé à chaque événement du
 * QueryCache, il doit rester quasi gratuit.
 */
export function isPerfEnabled(): boolean {
  if (enabledCache !== null) return enabledCache;
  let detected = false;
  try {
    detected = parsePerfSwitch(window.location.search, readStoredFlag());
  } catch {
    detected = false;
  }
  enabledCache = detected;
  return detected;
}

/**
 * Force l'état de l'interrupteur. `null` = re-détection au prochain appel.
 * Utilisé par `bootstrapPerf()`, par `perf.disable()` et par les tests.
 */
export function setPerfEnabled(value: boolean | null): void {
  enabledCache = value;
}

// ---------------------------------------------------------------------------
// Rédaction des noms (RGPD / confidentialité)
// ---------------------------------------------------------------------------

const UUID_RE =
  /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi;
const EMAIL_RE = /[^\s"',;<>@]+@[^\s"',;<>@]+\.[a-z]{2,}/gi;

/** Masque UUID et emails : un rapport perf n'a pas besoin de les porter. */
export function sanitizePerfName(name: string): string {
  return name.replace(UUID_RE, "<uuid>").replace(EMAIL_RE, "<email>");
}

// ---------------------------------------------------------------------------
// Analyse pure de la timeline
// ---------------------------------------------------------------------------

function isSpan(entry: PerfEntry): entry is PerfSpan {
  return entry.kind === "span";
}

/**
 * Plus longue suite de spans strictement enchaînés (fin du précédent ≤ début
 * du suivant). C'est la mesure du waterfall sériel : si elle contient 5
 * entrées, l'utilisateur a payé 5 aller-retours l'un après l'autre.
 * DP O(n²) — n est borné par MAX_PERF_ENTRIES.
 */
export function longestSerialChain(spans: PerfSpan[]): PerfSpan[] {
  if (spans.length === 0) return [];
  const sorted = [...spans].sort((a, b) => a.startMs - b.startMs);
  const best: number[] = sorted.map(() => 1);
  const totals: number[] = sorted.map((s) => s.durationMs);
  const previous: number[] = sorted.map(() => -1);

  for (let i = 1; i < sorted.length; i++) {
    for (let j = 0; j < i; j++) {
      if (sorted[j].endMs > sorted[i].startMs + SERIAL_TOLERANCE_MS) continue;
      const candidateLength = best[j] + 1;
      const candidateTotal = totals[j] + sorted[i].durationMs;
      if (
        candidateLength > best[i] ||
        (candidateLength === best[i] && candidateTotal > totals[i])
      ) {
        best[i] = candidateLength;
        totals[i] = candidateTotal;
        previous[i] = j;
      }
    }
  }

  let tail = 0;
  for (let i = 1; i < sorted.length; i++) {
    if (
      best[i] > best[tail] ||
      (best[i] === best[tail] && totals[i] > totals[tail])
    ) {
      tail = i;
    }
  }

  const chain: PerfSpan[] = [];
  for (let i = tail; i !== -1; i = previous[i]) chain.unshift(sorted[i]);
  return chain;
}

/** Nombre de spans qui chevauchent au moins un autre span (= parallélisme). */
function countParallelSpans(spans: PerfSpan[]): number {
  let count = 0;
  for (let i = 0; i < spans.length; i++) {
    const a = spans[i];
    const overlaps = spans.some(
      (b, j) => j !== i && b.startMs < a.endMs && b.endMs > a.startMs,
    );
    if (overlaps) count++;
  }
  return count;
}

/** Agrégats lisibles d'une timeline. Fonction pure (testée). */
export function summarizeSpans(
  entries: PerfEntry[],
  droppedEntries = 0,
): PerfSummary {
  const spans = entries.filter(isSpan);
  const chain = longestSerialChain(spans);
  const firstData = spans
    .filter((s) => typeof s.meta.rows === "number" && Number(s.meta.rows) > 0)
    .reduce<number | null>(
      (acc, s) => (acc === null || s.endMs < acc ? s.endMs : acc),
      null,
    );

  const firstStart =
    spans.length === 0 ? null : Math.min(...spans.map((s) => s.startMs));
  const lastEnd =
    spans.length === 0 ? null : Math.max(...spans.map((s) => s.endMs));

  return {
    spanCount: spans.length,
    markCount: entries.length - spans.length,
    droppedEntries,
    firstSpanStartMs: firstStart,
    lastSpanEndMs: lastEnd,
    spanWallMs:
      firstStart === null || lastEnd === null ? null : lastEnd - firstStart,
    serialChain: chain.map((s) => s.name),
    serialChainMs: chain.reduce((sum, s) => sum + s.durationMs, 0),
    parallelSpanCount: countParallelSpans(spans),
    timeToFirstDataMs: firstData,
  };
}

// ---------------------------------------------------------------------------
// Buffer + API
// ---------------------------------------------------------------------------

const entries: PerfEntry[] = [];
const pending = new Map<string, { startMs: number; meta: PerfMeta }>();
const listeners = new Set<() => void>();
let dropped = 0;
let version = 0;

const vitals: PerfVitals = {
  ttfbMs: null,
  domContentLoadedMs: null,
  fcpMs: null,
  lcpMs: null,
  longTaskCount: 0,
  longTaskTotalMs: 0,
  scripts: [],
  unsupported: [],
};

function now(): number {
  try {
    return performance.now();
  } catch {
    return Date.now();
  }
}

/** `performance.mark` best-effort : jsdom/Safari peuvent ne pas l'exposer. */
function nativeMark(name: string): void {
  try {
    if (typeof performance.mark === "function") {
      performance.mark(`${NATIVE_PREFIX}${name}`);
    }
  } catch {
    // Le traceur ne doit jamais casser l'app.
  }
}

function nativeMeasure(name: string, startMs: number, endMs: number): void {
  try {
    if (typeof performance.measure === "function") {
      performance.measure(`${NATIVE_PREFIX}${name}`, {
        start: startMs,
        duration: Math.max(0, endMs - startMs),
      });
    }
  } catch {
    // Idem : mesure best-effort.
  }
}

function notify(): void {
  version++;
  for (const listener of listeners) {
    try {
      listener();
    } catch {
      // Un abonné cassé ne doit pas empêcher les autres d'être notifiés.
    }
  }
}

function push(entry: PerfEntry): void {
  entries.push(entry);
  while (entries.length > MAX_PERF_ENTRIES) {
    entries.shift();
    dropped++;
  }
  notify();
}

export const perf: PerfApi = {
  get enabled() {
    return isPerfEnabled();
  },

  start(name, meta = {}) {
    if (!isPerfEnabled()) return;
    const key = sanitizePerfName(name);
    pending.set(key, { startMs: now(), meta });
    nativeMark(`${key}:start`);
  },

  end(name, meta = {}) {
    if (!isPerfEnabled()) return;
    const key = sanitizePerfName(name);
    const open = pending.get(key);
    if (!open) return;
    pending.delete(key);
    const endMs = now();
    nativeMark(`${key}:end`);
    nativeMeasure(key, open.startMs, endMs);
    push({
      kind: "span",
      name: key,
      startMs: open.startMs,
      endMs,
      durationMs: endMs - open.startMs,
      meta: { ...open.meta, ...meta },
    });
  },

  mark(name, meta = {}) {
    if (!isPerfEnabled()) return;
    const key = sanitizePerfName(name);
    nativeMark(key);
    push({ kind: "mark", name: key, startMs: now(), meta });
  },

  record(name, startMs, endMs, meta = {}) {
    if (!isPerfEnabled()) return;
    const key = sanitizePerfName(name);
    nativeMeasure(key, startMs, endMs);
    push({
      kind: "span",
      name: key,
      startMs,
      endMs,
      durationMs: endMs - startMs,
      meta,
    });
  },

  timeline() {
    return [...entries].sort((a, b) => a.startMs - b.startMs);
  },

  report() {
    const sorted = perf.timeline();
    return {
      enabled: isPerfEnabled(),
      generatedAtMs: Math.round(now()),
      location: readLocation(),
      summary: summarizeSpans(sorted, dropped),
      vitals: { ...vitals, scripts: [...vitals.scripts] },
      entries: sorted,
    };
  },

  toJSON() {
    return JSON.stringify(perf.report(), null, 2);
  },

  clear() {
    entries.length = 0;
    pending.clear();
    dropped = 0;
    notify();
  },

  version() {
    return version;
  },

  subscribe(listener) {
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  },

  disable() {
    try {
      window.localStorage.removeItem(PERF_STORAGE_KEY);
    } catch {
      // Stockage indisponible : l'état mémoire suffit.
    }
    setPerfEnabled(false);
    notify();
  },
};

function readLocation(): string | null {
  try {
    return sanitizePerfName(window.location.pathname + window.location.search);
  } catch {
    return null;
  }
}

// ---------------------------------------------------------------------------
// Web Vitals sans dépendance
// ---------------------------------------------------------------------------

function observe(
  type: string,
  handler: (entries: PerformanceEntryList) => void,
  buffered = true,
): void {
  try {
    const observer = new PerformanceObserver((list) => handler(list.getEntries()));
    observer.observe({ type, buffered });
  } catch {
    // `observe` lève sur un type inconnu (Safari : longtask, LCP…).
    if (!vitals.unsupported.includes(type)) vitals.unsupported.push(type);
  }
}

function collectNavigation(entry: PerformanceEntry): void {
  // PerformanceNavigationTiming n'est pas garanti par le type de base.
  const nav = entry as PerformanceNavigationTiming;
  if (typeof nav.responseStart === "number") {
    vitals.ttfbMs = Math.round(nav.responseStart);
  }
  if (typeof nav.domContentLoadedEventEnd === "number") {
    vitals.domContentLoadedMs = Math.round(nav.domContentLoadedEventEnd);
  }
}

function collectResource(entry: PerformanceEntry): void {
  const res = entry as PerformanceResourceTiming;
  if (!/\.(?:js|mjs)(?:\?|$)/.test(res.name)) return;
  vitals.scripts.push({
    name: res.name.split("/").pop() ?? res.name,
    durationMs: Math.round(res.duration),
    transferBytes:
      typeof res.transferSize === "number" ? res.transferSize : null,
    decodedBytes:
      typeof res.decodedBodySize === "number" ? res.decodedBodySize : null,
  });
}

/** Pose les observers Web Vitals. Chaque type est isolé (dégradation propre). */
function observeWebVitals(): void {
  observe("navigation", (list) => {
    for (const entry of list) collectNavigation(entry);
    notify();
  });
  observe("paint", (list) => {
    for (const entry of list) {
      if (entry.name === "first-contentful-paint") {
        vitals.fcpMs = Math.round(entry.startTime);
      }
    }
    notify();
  });
  observe("largest-contentful-paint", (list) => {
    for (const entry of list) vitals.lcpMs = Math.round(entry.startTime);
    notify();
  });
  observe("longtask", (list) => {
    for (const entry of list) {
      vitals.longTaskCount++;
      vitals.longTaskTotalMs += Math.round(entry.duration);
    }
    notify();
  });
  observe("resource", (list) => {
    for (const entry of list) collectResource(entry);
    notify();
  });
}

// ---------------------------------------------------------------------------
// Amorçage (appelé une seule fois par src/main.tsx)
// ---------------------------------------------------------------------------

let bootstrapped = false;

/**
 * Amorce le traceur si l'interrupteur est demandé :
 *   - persiste le flag quand il vient de l'URL (les redirections
 *     /app → /login → /app perdent la query string) ;
 *   - pose les observers Web Vitals ;
 *   - expose `window.__encrelioPerf` pour la console.
 * Ne fait RIEN (et n'écrit rien) quand l'interrupteur est éteint.
 */
export function bootstrapPerf(): boolean {
  if (bootstrapped) return isPerfEnabled();
  bootstrapped = true;
  if (!isPerfEnabled()) return false;

  try {
    if (window.localStorage.getItem(PERF_STORAGE_KEY) !== PERF_FLAG) {
      window.localStorage.setItem(PERF_STORAGE_KEY, PERF_FLAG);
    }
  } catch {
    // Stockage bloqué : le traçage reste actif pour cette page.
  }

  observeWebVitals();
  try {
    window.__encrelioPerf = perf;
  } catch {
    // Environnement sans window (tests Node) : rien à exposer.
  }
  return true;
}
